// Business-side guidance for the workbook's 32 browse categories. These are
// practical examples, not claims about an individual provider's qualifications,
// eligibility, specialist software, or the services Offlico has verified.
import { getProfessionPresentation } from './professionPresentation.js'

export const categoryContent = {
  'health-and-clinical-care': {
    summary: 'Appointments away from a fixed clinic, with location, client contact and follow-up details to keep in order.',
    intro: 'A mobile clinical appointment needs a clear time, location and client contact before the practitioner arrives. The business admin should stay connected afterwards.',
    before: 'Check the appointment location, contact details and any preparation the client needs from your own clinical process.',
    during: 'Keep the visit time and business contact information close at hand while you work away from the practice.',
    after: 'Keep the next appointment and business-side follow-up visible without relying on a paper diary.',
    fitNote: 'Offlico’s general client records do not replace specialist clinical records, consent, prescribing or professional compliance systems.',
  },
  'rehabilitation-and-physical-therapy': {
    summary: 'Assessments and return visits where timing, location and continuity matter.',
    intro: 'Home and workplace assessments often lead to further sessions. It helps to see the next visit, the right address and the client’s contact history together.',
    before: 'Arrange the assessment or return session around the client’s location and your existing diary.',
    during: 'Keep appointment details available while moving between homes, care settings or workplaces.',
    after: 'Track a practical next step and the next booking without losing it in separate messages.',
    fitNote: 'Use your own professional systems for clinical assessment, treatment plans, consent and regulated records.',
  },
  'mental-health-and-talking-therapies': {
    summary: 'Private sessions that depend on dependable timing and careful client communication.',
    intro: 'Mental health and therapy work needs predictable sessions and respectful communication. The scheduling and payment admin should not make that harder.',
    before: 'Keep the agreed session time, setting and contact route clear for the client.',
    during: 'See the booking information you need without turning your working day into a search through messages.',
    after: 'Keep the next session and any business-side payment follow-up in view.',
    fitNote: 'Offlico is not a therapy record, safeguarding, supervision or crisis-response system. Keep those processes in suitable specialist tools.',
  },
  'nutrition-and-complementary-therapies': {
    summary: 'Consultations and repeat sessions where preparation and follow-up can easily scatter.',
    intro: 'A consultation may involve questions before the visit and a follow-up afterwards. Keeping the practical details together makes the day easier to run.',
    before: 'Confirm the service, time and suitable setting before the appointment.',
    during: 'Keep client contact and visit details to hand, whether the session is at home or another agreed place.',
    after: 'Organise the next session and business admin in the same working diary.',
    fitNote: 'Professional assessment, treatment advice, consent and specialist records remain part of your own practice process.',
  },
  'hair-beauty-and-body-art': {
    summary: 'Client appointments shaped by preferences, preparation and repeat visits.',
    intro: 'Hair, beauty and body art appointments can depend on preparation, client preferences and a suitable setting. The booking and next visit need to make sense together.',
    before: 'Keep the booked service, visit address and preparation details clear before setting off.',
    during: 'Refer to customer details without piecing the appointment back together from old chats.',
    after: 'Make repeat bookings and payment follow-up easier to find.',
    fitNote: 'Keep patch tests, consent, age checks, premises registration or licensing where required, treatment records and other specialist requirements in your own approved process.',
  },
  'personal-style-and-clothing': {
    summary: 'Fittings and styling visits where measurements, preferences and return appointments matter.',
    intro: 'A fitting or styling appointment often starts with a brief and ends with another decision or visit. The business-side timeline should stay clear.',
    before: 'Record the agreed appointment, location and what the client expects to discuss.',
    during: 'Keep contact and booking details available while working at the client’s chosen setting.',
    after: 'Follow up on the next fitting, collection or payment without losing the thread.',
    fitNote: 'Offlico does not replace design, measurement, inventory or order-management software for specialist clothing work.',
  },
  'fitness-sport-and-movement': {
    summary: 'Sessions across homes, outdoor spaces and venues, often with a regular rhythm.',
    intro: 'Coaching sessions can move between locations and repeat each week. A tidy diary helps protect the time between clients.',
    before: 'Set the session time and location so the next part of the day is realistic.',
    during: 'See who is booked and where to go, even when sessions are spread across venues.',
    after: 'Keep the next session, reminder and payment admin connected.',
    fitNote: 'Offlico does not provide exercise programming, venue or pool suitability checks, medical screening, safeguarding or sports-governing-body compliance tools.',
  },
  'education-lessons-and-training': {
    summary: 'Lessons and training blocks that need consistent timing, locations and learner contact.',
    intro: 'Travelling between learners or venues leaves little room for diary confusion. The lesson schedule and family or client contact need to be easy to find.',
    before: 'Agree the lesson time, location and contact person before the session.',
    during: 'Keep the day’s learner and venue details in a single working schedule.',
    after: 'Plan the next lesson and manage the business-side follow-up.',
    fitNote: 'Curriculum planning, learner assessment, safeguarding and school reporting need their own appropriate processes.',
  },
  'coaching-and-personal-development': {
    summary: 'One-to-one sessions and programmes where the next meeting should not get lost.',
    intro: 'Coaching work can involve an introductory meeting, a series of sessions and follow-up between them. Clear scheduling helps the relationship run smoothly.',
    before: 'Set the meeting time and agreed location or contact method.',
    during: 'Keep client contact and the current booking easy to review.',
    after: 'Make the next session and invoice follow-up visible.',
    fitNote: 'Offlico is not a coaching curriculum, assessment or confidential case-management system.',
  },
  'childcare-and-family-support': {
    summary: 'Family-facing visits where reliable scheduling and careful communication are important.',
    intro: 'Families need to know when support is happening and who to contact. The business diary should stay straightforward even when visits recur.',
    before: 'Confirm the agreed visit time, location and the right family contact.',
    during: 'Keep the booking details available without relying on scattered messages.',
    after: 'Track the next visit and ordinary business admin clearly.',
    fitNote: 'Offlico does not replace safeguarding, DBS checks, care plans, consent or statutory childcare records.',
  },
  'home-care-disability-and-advocacy': {
    summary: 'Support visits and assessments where continuity and the right contact details matter.',
    intro: 'Home support often involves a person receiving care and someone else arranging it. Clear appointment and contact details make the business side less fragile.',
    before: 'Check the visit time, address and the contact who arranged the work.',
    during: 'Keep practical booking details close while working across homes or community settings.',
    after: 'Keep the next visit and any routine payment follow-up visible.',
    fitNote: 'Offlico is not a regulated care-management, medication, rota, safeguarding or clinical-record system.',
  },
  'pet-care-and-veterinary-services': {
    summary: 'Animal-related appointments where owner contact, location and return visits matter.',
    intro: 'Pet and veterinary work often means travelling to the animal, not waiting at a fixed premises. The booking, owner and destination need to stay together.',
    before: 'Confirm the animal owner’s contact, agreed service and location.',
    during: 'See the job details while moving between homes, clinics or outdoor settings.',
    after: 'Keep the next visit and ordinary business follow-up organised.',
    fitNote: 'Veterinary clinical records, prescriptions, animal welfare duties and specialist pet-care systems remain separate responsibilities.',
  },
  'equine-livestock-and-farm-services': {
    summary: 'Farm, yard and animal-site visits shaped by travel, access and recurring work.',
    intro: 'Work across farms, yards and other animal settings depends on the right location and a realistic route. A clear schedule matters when each site is different.',
    before: 'Check the site, contact person and agreed time before travelling.',
    during: 'Keep the day’s visits and access information together while working outdoors.',
    after: 'Record the next visit or business follow-up before moving to another site.',
    fitNote: 'Offlico does not replace veterinary records, livestock traceability, farm safety or specialist field-service systems.',
  },
  'building-and-construction': {
    summary: 'Site visits, quotes and staged work across different properties.',
    intro: 'A construction job may start with a survey and continue across several site visits. Losing the customer, address or next date slows everything down.',
    before: 'Keep the enquiry, site address and planned visit in one place.',
    during: 'See the agreed work and customer contact while moving between sites.',
    after: 'Track the next visit and business-side invoice or payment follow-up.',
    fitNote: 'Offlico is not a construction estimating, project-control, certification or site-safety platform.',
  },
  'plumbing-heating-and-water': {
    summary: 'Planned visits and callouts where addresses, timing and next steps change quickly.',
    intro: 'Callouts and planned maintenance need different diary space. The customer address, access details and next action should remain clear.',
    before: 'Capture the location, contact and reason for the visit before setting off.',
    during: 'Keep the appointment and customer details accessible on site.',
    after: 'Follow up on return work, invoices and the next visit.',
    fitNote: 'Gas safety, water regulations, compliance certificates and specialist job diagnostics need their own qualified processes.',
  },
  'electrical-security-and-installations': {
    summary: 'Callouts, installations and maintenance across homes and commercial sites.',
    intro: 'Installation work often combines an initial visit with later fitting or maintenance. A connected diary helps keep each stage visible.',
    before: 'Confirm the site, access contact and purpose of the callout or installation.',
    during: 'See the customer and appointment information while working on site.',
    after: 'Keep follow-up work and ordinary payment admin from disappearing into messages.',
    fitNote: 'Offlico does not replace electrical certification, security compliance, commissioning or site-safety records.',
  },
  'cleaning-and-restoration': {
    summary: 'One-off and repeat property work where access details and the next visit matter.',
    intro: 'Cleaning and restoration work can be regular, urgent or tied to a property handover. The booking, access details and client contact need to be easy to find.',
    before: 'Agree the property, visit time and any access arrangements.',
    during: 'Keep the job and customer details to hand at the property.',
    after: 'Make the next visit or invoice follow-up easy to track.',
    fitNote: 'Specialist restoration, hazardous-material handling and site-specific safety procedures remain outside general scheduling software.',
  },
  'waste-pests-and-environmental-services': {
    summary: 'Callouts and collections where the site, scope and return work must be clear.',
    intro: 'A pest or waste job may begin with a callout and need another visit or collection. Keeping the sequence visible helps the business run.',
    before: 'Confirm the site, access contact and broad reason for the visit.',
    during: 'Keep the agreed appointment and customer details available on site.',
    after: 'Track any return visit and business-side payment follow-up.',
    fitNote: 'Waste transfer documents, treatment records, environmental permits and safety compliance require specialist processes.',
  },
  'gardens-grounds-and-outdoor-spaces': {
    summary: 'Outdoor visits affected by access, travel, seasons and repeat maintenance.',
    intro: 'Garden and grounds work changes with the site and the season. A clear diary helps keep repeat visits and property details together.',
    before: 'Check the property, access arrangements and planned work before travelling.',
    during: 'See the job details while moving between outdoor sites.',
    after: 'Keep the next maintenance visit and payment follow-up visible.',
    fitNote: 'Weather decisions, machinery safety and specialist design or survey work remain the provider’s own responsibility.',
  },
  'property-design-surveying-and-inspection': {
    summary: 'Surveys and design visits where the property, brief and next decision matter.',
    intro: 'Property work often moves from an initial visit to a report, design or return discussion. The booking needs a clear link to the address and client.',
    before: 'Keep the survey or meeting time, property address and contact details together.',
    during: 'Review the appointment information on site without searching old messages.',
    after: 'Track the next meeting and business-side invoice follow-up.',
    fitNote: 'Offlico does not replace CAD, survey instruments, formal reports, valuations or professional certification.',
  },
  'vehicle-cycle-and-marine-services': {
    summary: 'Mobile vehicle, cycle and marine work where the right asset and location are essential.',
    intro: 'An on-site vehicle, cycle or marine visit needs a clear location, contact and description of the work. A handover or return visit may need its own slot.',
    before: 'Confirm the vehicle or equipment, location and contact before travelling.',
    during: 'Keep the booked work visible while moving between service locations.',
    after: 'Plan any return visit or handover and keep ordinary invoicing or payment admin in view.',
    fitNote: 'Offlico is not a repair diagnostic, parts catalogue, vehicle compliance or fleet-dispatch system.',
  },
  'technology-and-equipment-support': {
    summary: 'Equipment visits where the site, contact and next support action matter.',
    intro: 'On-site technical support may need a callout, a repair visit and a return check. A clear customer and appointment history keeps the business side moving.',
    before: 'Confirm the equipment location and the person who can provide access.',
    during: 'See the site visit and contact details while working away from the office.',
    after: 'Keep the next service visit or invoice follow-up visible.',
    fitNote: 'Offlico does not replace a help desk, remote monitoring, asset register or technical diagnostic platform.',
  },
  'industrial-and-commercial-field-services': {
    summary: 'Commercial site work organised around access, contracts and planned visits.',
    intro: 'Industrial field work involves more than a calendar slot. Sites, access rules, contacts and repeat service dates all affect the day.',
    before: 'Agree the site, point of contact and planned work block before travelling.',
    during: 'Keep the business-side appointment details available across sites.',
    after: 'Track the next service date and payment admin clearly.',
    fitNote: 'Offlico is not a permit-to-work, asset-maintenance, contract-management or industrial safety system.',
  },
  'fitting-repair-and-craft-services': {
    summary: 'Fittings and repairs that move from a first visit to parts, return work or collection.',
    intro: 'A repair or fitting can start with a quote and need a second visit. Keeping the client, object and next date connected helps avoid confusion.',
    before: 'Confirm the item or space, location and purpose of the first visit.',
    during: 'Keep customer and appointment details available during the fitting or repair.',
    after: 'Plan return work and ordinary payment follow-up in one diary.',
    fitNote: 'Offlico does not replace workshop production, parts inventory, measurement or specialist repair records.',
  },
  'transport-removals-and-deliveries': {
    summary: 'Journeys and collections where pickup, destination and timing have to line up.',
    intro: 'Transport work is shaped by a collection point and destination, not just an appointment time. Treat Offlico as a way to organise customer jobs, not a full transport system.',
    before: 'Record the customer, collection time and agreed locations.',
    during: 'Keep the booked job and contact details available while travelling.',
    after: 'Track the next customer job and payment follow-up.',
    fitNote: 'Offlico does not provide live dispatch, fleet tracking, proof-of-delivery or transport compliance tools.',
  },
  'land-agriculture-and-environmental-services': {
    summary: 'Field visits across land and farms where travel and site details matter.',
    intro: 'Field work can involve distant sites, changing conditions and a later report or visit. A simple appointment trail helps keep the business side in order.',
    before: 'Check the site location, access contact and planned time before travelling.',
    during: 'Keep customer and visit information accessible outdoors.',
    after: 'Record the next date and ordinary invoice follow-up.',
    fitNote: 'Offlico does not replace GIS, environmental sampling, statutory reporting or agricultural compliance systems.',
  },
  'photography-media-and-creative-work': {
    summary: 'Creative assignments with a time, location, client brief and delivery follow-up.',
    intro: 'A creative commission or site assignment has practical details on both sides of the work: a location and brief beforehand, then delivery and payment afterwards.',
    before: 'Confirm the brief, venue contact and booked time.',
    during: 'Keep the assignment and client details to hand on location.',
    after: 'Follow up on delivery dates, return work and invoices.',
    fitNote: 'Offlico does not replace editing, proofing galleries, licensing or digital asset management.',
  },
  'events-and-entertainment': {
    summary: 'Fixed-date events with venues, preparation and client handover.',
    intro: 'An event booking has a fixed date, a venue and often a preparation window. Offlico can help with the customer and schedule, but not the full event plan.',
    before: 'Keep the venue, client contact and agreed work block clear.',
    during: 'Review the event booking and practical contact details on the day.',
    after: 'Track a follow-up booking or invoice without losing the thread.',
    fitNote: 'Offlico does not provide ticketing, run-of-show, staffing, equipment logistics or event-production management.',
  },
  'food-drink-and-catering': {
    summary: 'Catering and food-service bookings with venues, timings and client details.',
    intro: 'A catering booking depends on when and where the work happens, with preparation before the event and payment afterwards.',
    before: 'Confirm the venue, client contact and agreed service window.',
    during: 'Keep the booking and location details easy to check on the day.',
    after: 'Track the next event and payment follow-up.',
    fitNote: 'Food safety, allergens, stock, kitchen production and licensing remain outside Offlico’s general business tools.',
  },
  'business-finance-and-legal-services': {
    summary: 'Client meetings and site assignments where dates and follow-up need care.',
    intro: 'Professional services can move between offices, homes and client sites. A clear meeting schedule and contact record help keep the business admin steady.',
    before: 'Confirm the meeting, location and appropriate client contact.',
    during: 'Keep the appointment details accessible while working away from your desk.',
    after: 'Track the next meeting and ordinary invoice follow-up.',
    fitNote: 'Offlico handles ordinary scheduling and business admin. Keep confidential client material, formal advice and regulated records in the appropriate professional systems.',
  },
  'organising-and-personal-assistance': {
    summary: 'Practical assignments where contacts, dates and next actions need to stay clear.',
    intro: 'Organising, household support and other client-facing assignments can involve several people and dates. A reliable booking trail helps protect the next step.',
    before: 'Confirm who arranged the work, the agreed time and any visit location.',
    during: 'Keep the agreed work and contact details easy to find.',
    after: 'Track the next action and any business-side payment follow-up.',
    fitNote: 'Offlico handles ordinary bookings and payment admin. Keep sensitive personal information, delegated decisions, secure access credentials and specialist records in appropriate systems.',
  },
  'specialist-business-field-services': {
    summary: 'Technical site assignments where the brief, access and next action matter.',
    intro: 'Specialist field assignments can involve an initial site visit, technical work and a report or return visit. The business-side schedule should remain clear.',
    before: 'Confirm the site, commissioning contact and planned work block.',
    during: 'Keep the booking and contact details available while on location.',
    after: 'Track a return visit and ordinary payment follow-up.',
    fitNote: 'Offlico handles ordinary bookings and payment admin. Keep technical findings, evidence custody, formal reports and safety or regulatory records in specialist systems.',
  },
};

// Short phrases for route descriptions. Keep these factual, category-specific
// and short enough to leave room for the profession name and Offlico context.
export const categorySeoSummaries = {
  'health-and-clinical-care': 'Practical scheduling for clinical work in suitable settings.',
  'rehabilitation-and-physical-therapy': 'Assessments, return visits and client details across locations.',
  'mental-health-and-talking-therapies': 'Private session scheduling and careful client communication.',
  'nutrition-and-complementary-therapies': 'Consultations, repeat sessions and practical follow-up.',
  'hair-beauty-and-body-art': 'Client bookings, preferences and repeat visits on the move.',
  'personal-style-and-clothing': 'Fittings, client preferences and return appointments.',
  'fitness-sport-and-movement': 'Recurring sessions across homes, outdoor spaces and venues.',
  'education-lessons-and-training': 'Lessons, locations and learner contact in a clear schedule.',
  'coaching-and-personal-development': 'Client sessions, next meetings and business follow-up.',
  'childcare-and-family-support': 'Family-facing visits, timing and careful communication.',
  'home-care-disability-and-advocacy': 'Support visits, contacts and return appointments.',
  'pet-care-and-veterinary-services': 'Owner contact, animal-related bookings and return visits.',
  'equine-livestock-and-farm-services': 'Farm and animal-site visits, travel and site contacts.',
  'building-and-construction': 'Site visits, staged jobs and customer follow-up.',
  'plumbing-heating-and-water': 'Planned visits, callouts and return work.',
  'electrical-security-and-installations': 'Callouts, installations and maintenance dates.',
  'cleaning-and-restoration': 'One-off and repeat visits, access details and next work.',
  'waste-pests-and-environmental-services': 'Callouts, collections and return site visits.',
  'gardens-grounds-and-outdoor-spaces': 'Outdoor work, property access and repeat maintenance.',
  'property-design-surveying-and-inspection': 'Property visits, client briefs and next meetings.',
  'vehicle-cycle-and-marine-services': 'Mobile service jobs, locations and follow-up.',
  'technology-and-equipment-support': 'Equipment visits, site contacts and next support actions.',
  'industrial-and-commercial-field-services': 'Commercial site visits, access contacts and repeat work.',
  'fitting-repair-and-craft-services': 'Fittings, repairs, return visits and customer contact.',
  'transport-removals-and-deliveries': 'Customer jobs, collection points and destinations.',
  'land-agriculture-and-environmental-services': 'Field visits, site details and later follow-up.',
  'photography-media-and-creative-work': 'Creative assignments, venues and delivery follow-up.',
  'events-and-entertainment': 'Fixed-date events, venues and client handovers.',
  'food-drink-and-catering': 'Catering bookings, venues and service windows.',
  'business-finance-and-legal-services': 'Client meetings, site assignments and business follow-up.',
  'organising-and-personal-assistance': 'Client assignments, contact details and next actions.',
  'specialist-business-field-services': 'Technical site assignments, access and return work.',
};

export function getProfessionCategoryContent(categorySlug) {
  const content = categoryContent[categorySlug];
  return content ? { ...content, seoSummary: categorySeoSummaries[categorySlug] } : null;
}

// The workbook includes appointments, site jobs, lessons, events and journeys.
// Use a noun that fits that pattern in both the rendered H1 and SEO metadata.
export function professionWorkNoun(bookingPattern = '') {
  const pattern = String(bookingPattern).toLocaleLowerCase('en-GB');
  if (pattern.includes('stay')) return 'stays';
  if (pattern.includes('tour')) return 'tours';
  if (pattern.includes('performance')) return 'performances';
  if (pattern.includes('journey') || pattern.includes('collection') || pattern.includes('move')) return 'jobs';
  if (pattern.includes('event')) return 'events';
  if (pattern.includes('callout') || pattern.includes('project') || pattern.includes('quote') || pattern.includes('repair') || pattern.includes('job')) return 'jobs';
  if (pattern.includes('lesson') || pattern.includes('session') || pattern.includes('workshop')) return 'sessions';
  if (pattern.includes('meeting') || pattern.includes('assignment') || pattern.includes('contract')) return 'client work';
  if (pattern.includes('visit')) return 'visits';
  return 'appointments';
}

const professionNounOverrides = {
  MP343: 'client work',
  MP344: 'client work',
  MP348: 'client work',
  MP349: 'client work',
  MP352: 'client work',
  MP353: 'client work',
  MP384: 'bookings',
  MP408: 'jobs',
  MP418: 'client work',
};

export function professionWorkNounFor(profession) {
  return professionNounOverrides[profession.id] || professionWorkNoun(getProfessionPresentation(profession).bookingPattern);
}

const practiceCategories = new Set([
  'health-and-clinical-care',
  'rehabilitation-and-physical-therapy',
  'mental-health-and-talking-therapies',
  'nutrition-and-complementary-therapies',
]);
const nonPracticeRoles = new Set(['MP015', 'MP021', 'MP026']);

export function usesPracticeManagementLanguage(profession) {
  return (practiceCategories.has(profession.categorySlug) && !nonPracticeRoles.has(profession.id)) || profession.id === 'MP049';
}

export function professionPageHeading(profession) {
  return `${profession.name}: keep ${professionWorkNounFor(profession)} and business admin together`;
}
